import React, { useEffect, useState } from 'react';
import http from '../http';
import C from '../colors';

function Field({ label, error, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: error ? '#c2412e' : C.ink3, marginBottom: 6, fontFamily: C.sans }}>
        {label}
      </div>
      {children}
      {error && <div style={{ fontSize: 11.5, color: '#c2412e', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const inputStyle = (focused, error) => ({
  width: '100%', boxSizing: 'border-box',
  background: '#fff',
  border: `1.5px solid ${error ? '#e88c7c' : focused ? C.brand : C.hair}`,
  borderRadius: 10, padding: '11px 14px',
  fontFamily: C.sans, fontSize: 14, color: C.ink,
  outline: 'none', transition: 'border-color .15s',
});

function Input({ name, value, onChange, type = 'text', placeholder, error, min, max }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      name={name} value={value} type={type} placeholder={placeholder}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      min={min} max={max}
      style={inputStyle(focused, !!error)}
    />
  );
}

function Textarea({ name, value, onChange, rows = 3, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      name={name} value={value} rows={rows} placeholder={placeholder}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{ ...inputStyle(focused, false), resize: 'vertical' }}
    />
  );
}

const InvoiceRequestDialog = ({ open, onClose, invoicableAmount }) => {
  const [formData, setFormData] = useState({
    amount: invoicableAmount || 0,
    title: '',
    type: 'personal',
    taxNumber: '',
    email: '',
    remark: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData({
      amount: invoicableAmount || 0,
      title: '',
      type: 'personal',
      taxNumber: '',
      email: '',
      remark: '',
    });
    setErrors({});
  }, [invoicableAmount, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const e = {};
    const amount = Number(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) e.amount = '金额必须大于 0';
    else if (amount > Number(invoicableAmount || 0)) e.amount = '金额不能超过可开票金额';
    if (!formData.title.trim()) e.title = '请填写发票抬头';
    if (formData.type === 'company' && !formData.taxNumber.trim()) e.taxNumber = '企业发票需要填写税号';
    if (!formData.email.trim()) e.email = '请填写接收邮箱';
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) e.email = '邮箱格式不正确';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await http.post('/user/invoice/request', formData);
      onClose(true);
    } catch {
      // error handled by http interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="invoice-request-dialog"
      onClick={() => onClose(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(26,20,23,.45)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.paper, borderRadius: 16,
          border: `1px solid ${C.hair}`,
          width: '100%', maxWidth: 480,
          boxShadow: '0 24px 60px -12px rgba(0,0,0,.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${C.hair}`,
        }}>
          <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 24, fontWeight: 400, color: C.ink }}>
            申请发票
          </h2>
        </div>

        {/* Form */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Type toggle */}
          <Field label="发票类型">
            <div style={{ display: 'flex', gap: 8 }}>
              {[['personal', '个人'], ['company', '企业']].map(([val, label]) => (
                <button key={val} onClick={() => setFormData(p => ({ ...p, type: val }))} style={{
                  flex: 1, padding: '9px 0', borderRadius: 9,
                  border: `1.5px solid ${formData.type === val ? C.brand : C.hair}`,
                  background: formData.type === val ? C.brandSoft : '#fff',
                  color: formData.type === val ? C.brand2 : C.ink3,
                  fontFamily: C.sans, fontSize: 13, fontWeight: formData.type === val ? 600 : 500,
                  cursor: 'pointer', transition: 'all .15s',
                }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="发票抬头" error={errors.title}>
            <Input name="title" value={formData.title} onChange={handleChange} error={errors.title} placeholder={formData.type === 'company' ? '企业名称' : '姓名'}/>
          </Field>

          {formData.type === 'company' && (
            <Field label="税号" error={errors.taxNumber}>
              <Input name="taxNumber" value={formData.taxNumber} onChange={handleChange} error={errors.taxNumber} placeholder="统一社会信用代码"/>
            </Field>
          )}

          <Field label="金额（元）" error={errors.amount}>
            <Input name="amount" value={formData.amount} onChange={handleChange} type="number" error={errors.amount} min={0} max={invoicableAmount}/>
          </Field>

          <Field label="接收邮箱" error={errors.email}>
            <Input name="email" value={formData.email} onChange={handleChange} type="email" error={errors.email} placeholder="发票将发送至此邮箱"/>
          </Field>

          <Field label="备注（可选）">
            <Textarea name="remark" value={formData.remark} onChange={handleChange} placeholder="如有特殊要求请在此说明"/>
          </Field>
        </div>

        {/* Actions */}
        <div style={{
          padding: '16px 24px 20px',
          borderTop: `1px solid ${C.hair}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end',
        }}>
          <button onClick={() => onClose(false)} disabled={isSubmitting} style={{
            padding: '9px 20px', borderRadius: 9,
            border: `1px solid ${C.hair2}`, background: '#fff',
            color: C.ink2, fontFamily: C.sans, fontSize: 13,
            cursor: 'pointer',
          }}>
            取消
          </button>
          <button data-testid="invoice-request-submit" onClick={handleSubmit} disabled={isSubmitting} style={{
            padding: '9px 24px', borderRadius: 9, border: 'none',
            background: isSubmitting ? C.hair2 : C.brand,
            color: '#fff', fontFamily: C.sans, fontSize: 13, fontWeight: 500,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            boxShadow: isSubmitting ? 'none' : `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(255,122,162,.5)`,
            transition: 'all .15s',
          }}>
            {isSubmitting ? '提交中…' : '提交申请'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceRequestDialog;
